import React from 'react';
import { Link } from 'react-router-dom';
import { 
  BriefcaseIcon,
  AcademicCapIcon,
  BuildingOfficeIcon,
  ShieldCheckIcon
} from '@heroicons/react/24/outline';

// Import data
import { EXECUTIVES } from '../../data/executives';

const ExecutivesPage: React.FC = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white shadow-sm">
        <div className="section-padding">
          <div className="text-center">
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
              Executive Leadership
            </h1>
            <p className="text-lg sm:text-xl text-gray-600 max-w-3xl mx-auto">
              Meet the dedicated professionals leading CSEAN towards a secure digital future for Nigeria
            </p>
          </div>
        </div>
      </div>

      {/* Executives Grid */}
      <div className="section-padding bg-gray-50">
        <div className="container-max">
          {/* First row - President and Vice President */}
          <div className="flex flex-col md:flex-row justify-center gap-8 mb-8">
            {EXECUTIVES.slice(0, 2).map((executive) => (
              <Link
                key={executive.id}
                to={`/about/executives/${executive.id}`}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer block w-full max-w-sm"
              >
                {/* Executive Image */}
                <div className="relative h-80 sm:h-96 bg-gray-200 overflow-hidden">
                  <img
                    src={executive.image}
                    alt={executive.name}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      // Try .jpeg extension if .jpg fails
                      if (!e.currentTarget.src.includes('.jpeg')) {
                        const jpegSrc = e.currentTarget.src.replace('.jpg', '.jpeg');
                        e.currentTarget.src = jpegSrc;
                      } else {
                        // Fallback to placeholder if image still not found
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${executive.name}&size=400&background=1e40af&color=fff`;
                      }
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="w-6 h-6 text-white mr-2" />
                      <p className="text-white text-sm font-medium">{executive.position}</p>
                    </div>
                  </div>
                </div>

                {/* Executive Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{executive.name}</h3>
                  <p className="text-primary-600 font-semibold mb-4">{executive.position}</p>
                  <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                    {Array.isArray(executive.bio) ? executive.bio[0] : executive.bio}
                  </p>
                </div>
              </Link>
            ))}
          </div>

          {/* Remaining executives - 3 column grid */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {EXECUTIVES.slice(2).map((executive) => (
              <Link
                key={executive.id}
                to={`/about/executives/${executive.id}`}
                className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 cursor-pointer block"
              >
                {/* Executive Image */}
                <div className="relative h-80 sm:h-96 bg-gray-200 overflow-hidden">
                  <img
                    src={executive.image}
                    alt={executive.name}
                    className="w-full h-full object-cover object-top"
                    onError={(e) => {
                      // Try .jpeg extension if .jpg fails
                      if (!e.currentTarget.src.includes('.jpeg')) {
                        const jpegSrc = e.currentTarget.src.replace('.jpg', '.jpeg');
                        e.currentTarget.src = jpegSrc;
                      } else {
                        // Fallback to placeholder if image still not found
                        e.currentTarget.src = `https://ui-avatars.com/api/?name=${executive.name}&size=400&background=1e40af&color=fff`;
                      }
                    }}
                  />
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/50 to-transparent p-4">
                    <div className="flex items-center">
                      <ShieldCheckIcon className="w-6 h-6 text-white mr-2" />
                      <p className="text-white text-sm font-medium">{executive.position}</p>
                    </div>
                  </div>
                </div>

                {/* Executive Info */}
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-2">{executive.name}</h3>
                  <p className="text-primary-600 font-semibold mb-4">{executive.position}</p>
                  <p className="text-gray-600 text-sm line-clamp-3 mb-4">
                    {Array.isArray(executive.bio) ? executive.bio[0] : executive.bio}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </div>

      {/* Detailed Profile Modal (Implementation for future) */}
      <div className="bg-white section-padding">
        <div className="container-max text-center">
          <BuildingOfficeIcon className="w-16 h-16 text-primary-600 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Leadership Excellence</h2>
          <p className="text-gray-600 max-w-2xl mx-auto mb-6">
            Our executive team brings decades of combined experience in cybersecurity, 
            dedicated to advancing the profession and protecting Nigeria's digital infrastructure.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
            <div className="text-center">
              <AcademicCapIcon className="w-12 h-12 text-primary-600 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Expertise</h3>
              <p className="text-sm text-gray-600">
                World-class cybersecurity professionals with international certifications
              </p>
            </div>
            <div className="text-center">
              <ShieldCheckIcon className="w-12 h-12 text-primary-600 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Experience</h3>
              <p className="text-sm text-gray-600">
                Decades of collective experience in cybersecurity leadership
              </p>
            </div>
            <div className="text-center">
              <BriefcaseIcon className="w-12 h-12 text-primary-600 mx-auto mb-3" />
              <h3 className="font-bold text-gray-900 mb-2">Commitment</h3>
              <p className="text-sm text-gray-600">
                Passionate about advancing cybersecurity in Nigeria
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExecutivesPage;